import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HistoryIcon from '@mui/icons-material/History';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch, buildApiHeaders } from '../api/client';
import { useBusiness } from '../contexts/BusinessContext';
import CanvasHierarchyPanel from './CanvasHierarchyPanel';
import CanvasPropertiesPanel, { type InspectorSelection } from './CanvasPropertiesPanel';
import AdvancedInspectorPanel from './AdvancedInspectorPanel';
import SimpleBuilderInspectorPanel from './SimpleBuilderInspectorPanel';
import SimplePropertiesPanel from './SimplePropertiesPanel';
import type { UiField, UiSchemaJson, UiSection, UiTab } from '../types/uiSchema';
import { STUDIO_LAYOUT, STUDIO_PANEL } from './studioDesign';
import {
  findUiSectionInTabs,
  mergeUiSchemaForSave,
  patchSectionInTabs,
  syncTabsFromFormSchema,
  upsertUiFieldInList,
  validateUiSchemaForSave,
} from '../utils/canvasUiSchema';

type ConfigRow = { id: string; name: string };
type FieldRow = {
  id: string;
  label?: string;
  name?: string;
  type?: string;
  required?: boolean;
  allowed_values?: string[];
  default_value?: unknown;
  accept?: string;
};
type UiSchemaLike = { field_refs?: string[] } & Record<string, unknown>;

const FIELD_TYPES = [
  'text', 'textarea', 'number', 'date',
  'single_select', 'multi_select', 'radio',
  'checkbox', 'boolean', 'email', 'phone',
  'link', 'address', 'currency', 'ref_field', 'file', 'space',
] as const;

function fieldAccentColor(type: string | undefined, theme: Theme): string {
  const p = theme.palette;
  switch (type) {
    case 'number':
      return p.info.main;
    case 'date':
      return p.secondary.main;
    case 'single_select':
    case 'multi_select':
      return p.warning.main;
    case 'checkbox':
    case 'boolean':
      return p.success.main;
    case 'textarea':
      return p.error.main;
    default:
      return p.primary.main;
  }
}

function SortableFieldRow({
  id,
  accentColor,
  children,
}: {
  id: string;
  accentColor: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.72 : 1,
  };
  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 8 : 0}
      sx={{
        p: 0,
        borderRadius: STUDIO_LAYOUT.panelRadius,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'stretch',
        gap: 0,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        boxShadow: isDragging ? '0 12px 40px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
        '&:hover': {
          borderColor: alpha(accentColor, 0.55),
          boxShadow: '0 4px 18px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Box
        sx={{
          width: 5,
          flexShrink: 0,
          bgcolor: accentColor,
          opacity: isDragging ? 1 : 0.9,
        }}
        aria-hidden
      />
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          px: 1,
          py: 1.25,
          display: 'flex',
          alignItems: 'flex-start',
          color: 'text.secondary',
          touchAction: 'none',
          bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
          '&:active': { cursor: 'grabbing' },
        }}
        aria-label="Drag to reorder"
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, py: 1, pr: 1 }}>{children}</Box>
    </Paper>
  );
}

export default function AdminCanvasPage() {
  const { activeConfigId, activeTenantId, setActiveConfigId } = useBusiness();
  const [configs, setConfigs] = useState<ConfigRow[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [formSchema, setFormSchema] = useState<Record<string, unknown> | null>(null);
  const [uiSchema, setUiSchema] = useState<UiSchemaLike | null>(null);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [newSectionId, setNewSectionId] = useState('');
  const [newSectionTitle, setNewSectionTitle] = useState('');

  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [newFieldId, setNewFieldId] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<string>('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldOptions, setNewFieldOptions] = useState('');

  const [editFieldDialogOpen, setEditFieldDialogOpen] = useState(false);
  const [editFieldId, setEditFieldId] = useState('');
  const [editFieldLabel, setEditFieldLabel] = useState('');
  const [editFieldType, setEditFieldType] = useState<string>('text');
  const [editFieldRequired, setEditFieldRequired] = useState(false);
  const [editFieldOptions, setEditFieldOptions] = useState('');

  const [deleteFieldOpen, setDeleteFieldOpen] = useState(false);
  const [deleteFieldTarget, setDeleteFieldTarget] = useState<{ id: string; label: string } | null>(null);

  const [deleteSectionOpen, setDeleteSectionOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [previewNonce, setPreviewNonce] = useState(0);
  const [canvasTabs, setCanvasTabs] = useState<UiTab[]>([]);
  const [inspectorSelection, setInspectorSelection] = useState<InspectorSelection>({ kind: null });
  const [isPropsPanelOpen, setIsPropsPanelOpen] = useState(true);
  const [autoLayoutMode, setAutoLayoutMode] = useState<'rules' | 'ai' | 'hybrid'>('hybrid');
  const [isAutoLayouting, setIsAutoLayouting] = useState(false);
  const [autoLayoutSource, setAutoLayoutSource] = useState<string>('');
  const [autoLayoutDecisions, setAutoLayoutDecisions] = useState<string[]>([]);
  const [autoLayoutWarnings, setAutoLayoutWarnings] = useState<string[]>([]);
  const [autoLayoutHistoryOpen, setAutoLayoutHistoryOpen] = useState(false);
  const [autoLayoutHistoryLoading, setAutoLayoutHistoryLoading] = useState(false);
  const [autoLayoutHistoryItems, setAutoLayoutHistoryItems] = useState<Array<{
    id: number;
    created_at: string | null;
    user_id: number;
    client_source: string;
    meta_json: any;
  }>>([]);
  const [studioMode, setStudioMode] = useState<'simple' | 'advanced'>('simple');
  const [forcedAdvanced, setForcedAdvanced] = useState(false);

  const theme = useTheme();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadConfigs = async () => {
    if (!activeTenantId) {
      setConfigs([]);
      return;
    }
    const resp = await apiFetch('/api/form-configs');
    if (!resp.ok) { setError(await resp.text()); return; }
    setConfigs((await resp.json()) as ConfigRow[]);
  };

  const loadConfig = async (configId: string) => {
    if (!configId || !activeTenantId) return;
    const resp = await apiFetch(`/api/form-configs/${configId}`);
    if (!resp.ok) { setError(await resp.text()); return; }
    const cfg = await resp.json();
    setFormSchema(cfg.form_schema_json || { sections: [] });
    const ui = (cfg.ui_schema_json || {}) as UiSchemaLike;
    setUiSchema(ui);
    const tabs = (ui as UiSchemaJson).tabs;
    setCanvasTabs(Array.isArray(tabs) ? tabs.map((t, i) => ({ ...t, order: t.order ?? i })) : []);
    setInspectorSelection({ kind: null });
  };

  useEffect(() => {
    void loadConfigs();
  }, [activeTenantId]);
  useEffect(() => {
    if (selectedConfigId && activeTenantId) void loadConfig(selectedConfigId);
  }, [selectedConfigId, activeTenantId]);

  useEffect(() => {
    if (!configs.length) {
      setSelectedConfigId('');
      setSelectedSectionIndex(0);
      setActiveConfigId(null);
      return;
    }
    const ids = new Set(configs.map((c) => c.id));
    if (selectedConfigId && ids.has(selectedConfigId)) return;
    const nextId = ids.has(activeConfigId || '') ? (activeConfigId as string) : configs[0].id;
    setSelectedConfigId(nextId);
    if (nextId !== activeConfigId) setActiveConfigId(nextId);
  }, [configs, selectedConfigId, activeConfigId, setActiveConfigId]);

  const fields = useMemo(() => {
    const sections = (formSchema?.sections as Array<Record<string, unknown>> | undefined) || [];
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const section = sections[safeIdx] || {};
    return ((section.fields as FieldRow[] | undefined) || []).slice();
  }, [formSchema, selectedSectionIndex]);

  const sortableIds = useMemo(
    () => fields.map((f) => `${selectedSectionIndex}::${f.id}`),
    [fields, selectedSectionIndex],
  );

  const groupFields = useMemo(() => {
    const sections = (formSchema?.sections as Array<Record<string, unknown>> | undefined) || [];
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const section = sections[safeIdx] || {};
    const groups = (section as { groups?: unknown }).groups;
    if (!Array.isArray(groups)) return [];
    const out: Array<FieldRow & { groupName?: string }> = [];
    for (const g of groups) {
      const gName = (g && (g as { name?: string }).name || (g as { title?: string }).title || (g as { id?: string }).id)
        ? String((g as { name?: string }).name || (g as { title?: string }).title || (g as { id?: string }).id)
        : undefined;
      const gFields = Array.isArray((g as { fields?: unknown })?.fields) ? (g as { fields: FieldRow[] }).fields : [];
      for (const f of gFields) {
        if (!f || typeof f !== 'object') continue;
        const fid = (f as FieldRow).id;
        if (!fid) continue;
        out.push({
          id: String(fid),
          label: (f as FieldRow).label ?? (f as FieldRow).name ?? String(fid),
          type: (f as FieldRow).type,
          required: Boolean((f as FieldRow).required),
          groupName: gName,
        });
      }
    }
    return out;
  }, [formSchema, selectedSectionIndex]);

  const currentSectionLabel = useMemo(() => {
    const sections = (formSchema?.sections as Array<Record<string, unknown>> | undefined) || [];
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const sec = sections[safeIdx];
    if (!sec) return '';
    return String((sec as { title?: string }).title ?? (sec as { name?: string }).name ?? sec.id ?? `Section ${safeIdx + 1}`);
  }, [formSchema, selectedSectionIndex]);

  const currentSectionStableId = useMemo(() => {
    const sections = (formSchema?.sections as Array<Record<string, unknown>> | undefined) || [];
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const sec = sections[safeIdx];
    return sec ? String(sec.id ?? '') : '';
  }, [formSchema, selectedSectionIndex]);

  const selectedConfigName = useMemo(
    () => configs.find((c) => c.id === selectedConfigId)?.name ?? '',
    [configs, selectedConfigId],
  );

  const previewIframeSrc = useMemo(() => {
    if (!selectedConfigId || typeof window === 'undefined') return '';
    const base = import.meta.env.BASE_URL || '/';
    const pathPrefix = base === '/' ? '' : base.replace(/\/$/, '');
    return `${window.location.origin}${pathPrefix}/runtime-form/${selectedConfigId}?embed=1&nonce=${previewNonce}`;
  }, [selectedConfigId, previewNonce]);

  useEffect(() => {
    setSelectedSectionIndex(0);
    setInspectorSelection({ kind: null });
  }, [selectedConfigId]);

  const uiSchemaJson = useMemo(() => (uiSchema || {}) as UiSchemaJson, [uiSchema]);

  const currentFieldRef = useMemo(() => {
    if (inspectorSelection.kind !== 'field' || !currentSectionStableId) return '';
    return `${currentSectionStableId}.${inspectorSelection.fieldId}`;
  }, [inspectorSelection, currentSectionStableId]);

  const selectedUiField: UiField | null = useMemo(() => {
    if (!currentFieldRef) return null;
    const row = uiSchemaJson.fields?.find((f) => String(f.field_ref) === currentFieldRef);
    return row ? { ...row } : { field_ref: currentFieldRef, visibility: 'always', width: 'half' };
  }, [uiSchemaJson.fields, currentFieldRef]);

  const selectedSchemaField: FieldRow | null = useMemo(() => {
    if (inspectorSelection.kind !== 'field') return null;
    const f = fields.find((x) => String(x.id) === String(inspectorSelection.fieldId));
    return f ? { ...f } : null;
  }, [fields, inspectorSelection]);

  const selectedUiSectionRow = useMemo(
    () => (currentSectionStableId ? findUiSectionInTabs(canvasTabs, currentSectionStableId) : null),
    [canvasTabs, currentSectionStableId],
  );

  const patchUiField = (patch: Partial<UiField>) => {
    if (!currentFieldRef) return;
    setUiSchema((prev) => {
      const base = (prev || {}) as UiSchemaJson;
      return { ...base, fields: upsertUiFieldInList(base.fields, currentFieldRef, patch) };
    });
  };

  const patchSchemaField = (patch: Partial<FieldRow>) => {
    if (!formSchema || inspectorSelection.kind !== 'field') return;
    const schema = JSON.parse(JSON.stringify(formSchema)) as { sections?: Array<{ fields?: FieldRow[] }> };
    const sections = schema.sections || [];
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const sec = sections[safeIdx];
    if (!sec?.fields) return;
    const idx = sec.fields.findIndex((x) => x.id === inspectorSelection.fieldId);
    if (idx < 0) return;
    sec.fields[idx] = { ...sec.fields[idx], ...patch, id: sec.fields[idx].id };
    setFormSchema(schema as unknown as Record<string, unknown>);
    const fieldId = sec.fields[idx].id;
    const nextType = String((patch.type || sec.fields[idx].type || '')).toLowerCase();
    const fieldRef = currentSectionStableId ? `${currentSectionStableId}.${fieldId}` : '';
    if (fieldRef) {
      if (nextType === 'single_select') patchUiField({ widget: 'dropdown' });
      else if (nextType === 'multi_select') patchUiField({ widget: 'dropdown' });
      else if (nextType === 'radio') patchUiField({ widget: 'radio' });
      else if (nextType === 'date') patchUiField({ widget: 'date' });
      else if (nextType === 'number' || nextType === 'currency') patchUiField({ widget: 'number' });
      else if (nextType === 'textarea') patchUiField({ widget: 'textarea' });
      else if (nextType === 'checkbox' || nextType === 'boolean') patchUiField({ widget: 'checkbox' });
      else if (nextType === 'file') patchUiField({ widget: 'file' });
      else patchUiField({ widget: 'text_input' });
    }
  };

  const patchCurrentSectionUi = (patch: Partial<UiSection>) => {
    if (!formSchema || !currentSectionStableId) return;
    setCanvasTabs((prev) => {
      const base = prev.length ? prev : syncTabsFromFormSchema(formSchema);
      return patchSectionInTabs(base, currentSectionStableId, patch);
    });
  };

  /** Keep form_schema section title/name in sync with Simple mode section editor + UI tabs. */
  const patchCurrentSectionSchema = (patch: { title?: string }) => {
    if (!formSchema) return;
    const schema = JSON.parse(JSON.stringify(formSchema)) as { sections?: Array<Record<string, unknown>> };
    const sections = schema.sections || [];
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const sec = sections[safeIdx];
    if (!sec) return;
    if (patch.title !== undefined) {
      sec.title = patch.title;
      sec.name = patch.title;
    }
    setFormSchema(schema as unknown as Record<string, unknown>);
  };

  const patchSectionSimple = (patch: Partial<UiSection>) => {
    patchCurrentSectionUi(patch);
    if (patch.title !== undefined) patchCurrentSectionSchema({ title: patch.title });
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !formSchema) return;
    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
    const schema = JSON.parse(JSON.stringify(formSchema)) as { sections?: Array<{ fields?: FieldRow[] }> };
    if (!schema.sections?.length) return;
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, schema.sections.length - 1));
    const sec = schema.sections[safeIdx];
    if (!sec?.fields) return;
    sec.fields = arrayMove(sec.fields, oldIndex, newIndex);
    setFormSchema(schema as unknown as Record<string, unknown>);
  };

  const save = async () => {
    if (!selectedConfigId || !formSchema) return;
    setError('');
    setInfo('');
    const nextUi = mergeUiSchemaForSave(formSchema, uiSchemaJson, canvasTabs.length ? canvasTabs : undefined);
    const validationErrors = validateUiSchemaForSave(formSchema, nextUi);
    if (validationErrors.length > 0) {
      setError(`Cannot save layout: ${validationErrors[0]}`);
      return;
    }
    const headers = new Headers(buildApiHeaders({ 'Content-Type': 'application/json' }));
    headers.set('X-Canvas-Source', 'true');
    const resp = await apiFetch(`/api/form-configs/${selectedConfigId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ form_schema_json: formSchema, ui_schema_json: nextUi }),
    });
    if (!resp.ok) { setError(await resp.text()); return; }
    setUiSchema(nextUi as UiSchemaLike);
    if (nextUi.tabs?.length) setCanvasTabs(nextUi.tabs.map((t, i) => ({ ...t, order: t.order ?? i })));
    setInfo('Canvas saved.');
    // Force the runtime iframe to reload so per-field UI edits are visible immediately.
    setPreviewNonce((n) => n + 1);
  };

  const runAutoLayout = async () => {
    if (!selectedConfigId) return;
    setError('');
    setInfo('');
    setIsAutoLayouting(true);
    const resp = await apiFetch(`/api/form-configs/${selectedConfigId}/generate-ui-schema?mode=${encodeURIComponent(autoLayoutMode)}`, {
      method: 'POST',
    });
    setIsAutoLayouting(false);
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    const data = (await resp.json()) as {
      ui_schema_json?: UiSchemaJson;
      source?: string;
      decisions?: string[];
      warnings?: string[];
      warning?: string;
    };
    const ui = (data.ui_schema_json || {}) as UiSchemaLike;
    setUiSchema(ui);
    const tabs = (ui as UiSchemaJson).tabs;
    setCanvasTabs(Array.isArray(tabs) ? tabs.map((t, i) => ({ ...t, order: t.order ?? i })) : []);
    setAutoLayoutSource(String(data.source || autoLayoutMode));
    setAutoLayoutDecisions(Array.isArray(data.decisions) ? data.decisions : []);
    const warnings = Array.isArray(data.warnings) ? data.warnings : [];
    if (data.warning) warnings.push(String(data.warning));
    setAutoLayoutWarnings(warnings);
    setInfo('Auto-layout generated and applied. Save canvas to persist.');
    setPreviewNonce((n) => n + 1);
  };

  const loadAutoLayoutHistory = async () => {
    if (!selectedConfigId) return;
    setAutoLayoutHistoryLoading(true);
    setError('');
    try {
      const resp = await apiFetch(`/api/form-configs/${selectedConfigId}/canvas-audit-events?limit=100`);
      if (!resp.ok) {
        setError(await resp.text());
        return;
      }
      const data = (await resp.json()) as { items?: any[] };
      const items = Array.isArray(data.items) ? data.items : [];
      const filtered = items.filter((x) => String(x?.client_source || '') === 'auto_layout');
      setAutoLayoutHistoryItems(filtered);
    } finally {
      setAutoLayoutHistoryLoading(false);
    }
  };

  const isAdvancedLayoutDetected = useMemo(() => {
    const tabs = canvasTabs || [];
    if (tabs.length > 1) return true;
    if (tabs.some((t) => Array.isArray(t.sub_tabs) && t.sub_tabs.length > 0)) return true;
    return false;
  }, [canvasTabs]);

  useEffect(() => {
    if (studioMode === 'simple' && isAdvancedLayoutDetected) {
      setStudioMode('advanced');
      setForcedAdvanced(true);
    }
  }, [isAdvancedLayoutDetected, studioMode]);
  useEffect(() => {
    setForcedAdvanced(false);
  }, [selectedConfigId]);
  useEffect(() => {
    // Default to Simple per-config. Advanced is enforced by detection above.
    setStudioMode('simple');
  }, [selectedConfigId]);

  const addSection = () => {
    if (!formSchema) return;
    const sid = newSectionId.trim().replace(/\s+/g, '_');
    const title = newSectionTitle.trim();
    if (!sid || !title) {
      setError('Section id and title are required.');
      return;
    }
    const sections = ([...(formSchema.sections as Array<Record<string, unknown>> | undefined) || []]) as Array<Record<string, unknown>>;
    if (sections.some((s) => String(s.id) === sid)) {
      setError('Section id already exists.');
      return;
    }
    sections.push({ id: sid, title, name: title, fields: [] });
    setFormSchema({ ...formSchema, sections });
    setNewSectionId('');
    setNewSectionTitle('');
    setSectionDialogOpen(false);
    setSelectedSectionIndex(sections.length - 1);
    setInfo('Section added (save to persist).');
  };

  const addField = () => {
    if (!formSchema) return;
    const fid = newFieldId.trim().replace(/\s+/g, '_');
    const label = newFieldLabel.trim();
    if (!fid || !label) {
      setError('Field id and label are required.');
      return;
    }
    const schema = JSON.parse(JSON.stringify(formSchema)) as { sections?: Array<{ id?: string; fields?: FieldRow[] }> };
    const sections = schema.sections || [];
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const sec = sections[safeIdx];
    if (!sec) {
      setError('No section selected.');
      return;
    }
    sec.fields = sec.fields || [];
    if (sec.fields.some((f) => f.id === fid)) {
      setError('Field id already exists in this section.');
      return;
    }
    const row: FieldRow = {
      id: fid,
      label,
      type: newFieldType,
      required: newFieldRequired,
    };
    if (newFieldType === 'single_select' || newFieldType === 'multi_select') {
      const opts = newFieldOptions.split(',').map((s) => s.trim()).filter(Boolean);
      if (opts.length === 0) {
        setError('Select-type fields need at least one option (comma-separated).');
        return;
      }
      row.allowed_values = opts;
    }
    sec.fields.push(row);
    setFormSchema(schema as unknown as Record<string, unknown>);
    if (currentSectionStableId) {
      const ref = `${currentSectionStableId}.${fid}`;
      const t = String(newFieldType || '').toLowerCase();
      const patch: Partial<UiField> =
        t === 'single_select' ? { widget: 'dropdown' }
          : t === 'multi_select' ? { widget: 'dropdown' }
            : t === 'radio' ? { widget: 'radio' }
              : t === 'date' ? { widget: 'date' }
                : (t === 'number' || t === 'currency') ? { widget: 'number' }
                  : t === 'textarea' ? { widget: 'textarea' }
                    : (t === 'checkbox' || t === 'boolean') ? { widget: 'checkbox' }
                      : t === 'file' ? { widget: 'file' }
                      : { widget: 'text_input' };
      setUiSchema((prev) => {
        const base = (prev || {}) as UiSchemaJson;
        return { ...base, fields: upsertUiFieldInList(base.fields, ref, patch) };
      });
    }
    setNewFieldId('');
    setNewFieldLabel('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setNewFieldOptions('');
    setFieldDialogOpen(false);
    setInfo('Field added (save to persist).');
  };

  const quickAddFieldType = (type: string) => {
    if (!formSchema) return;
    const sections = (formSchema.sections as Array<Record<string, unknown>> | undefined) || [];
    if (!sections.length) {
      setError('Add a section first.');
      return;
    }
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const sec = sections[safeIdx] || {};
    const existing = ((sec as { fields?: FieldRow[] }).fields || []).map((f) => String(f.id));
    const base = String(type || 'field').trim().replace(/\W+/g, '_') || 'field';
    let n = existing.length + 1;
    let fid = `${base}_${n}`;
    while (existing.includes(fid)) {
      n += 1;
      fid = `${base}_${n}`;
    }
    const label = base.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

    if (type === 'single_select' || type === 'multi_select' || type === 'radio') {
      setNewFieldId(fid);
      setNewFieldLabel(label);
      setNewFieldType(type);
      setNewFieldRequired(false);
      setNewFieldOptions('Option 1, Option 2');
      setFieldDialogOpen(true);
      return;
    }

    setNewFieldId(fid);
    setNewFieldLabel(label);
    setNewFieldType(type);
    setNewFieldRequired(false);
    setNewFieldOptions('');
    addField();
    setInspectorSelection({ kind: 'field', fieldId: fid });
    const effectiveType = String(type || '').toLowerCase();
    if (currentSectionStableId) {
      const ref = `${currentSectionStableId}.${fid}`;
      if (effectiveType === 'date') setUiSchema((prev) => ({ ...(prev || {}), fields: upsertUiFieldInList(((prev || {}) as UiSchemaJson).fields, ref, { widget: 'date' }) }));
      else if (effectiveType === 'number' || effectiveType === 'currency') setUiSchema((prev) => ({ ...(prev || {}), fields: upsertUiFieldInList(((prev || {}) as UiSchemaJson).fields, ref, { widget: 'number' }) }));
      else if (effectiveType === 'textarea') setUiSchema((prev) => ({ ...(prev || {}), fields: upsertUiFieldInList(((prev || {}) as UiSchemaJson).fields, ref, { widget: 'textarea' }) }));
      else if (effectiveType === 'checkbox' || effectiveType === 'boolean') setUiSchema((prev) => ({ ...(prev || {}), fields: upsertUiFieldInList(((prev || {}) as UiSchemaJson).fields, ref, { widget: 'checkbox' }) }));
      else if (effectiveType === 'file') setUiSchema((prev) => ({ ...(prev || {}), fields: upsertUiFieldInList(((prev || {}) as UiSchemaJson).fields, ref, { widget: 'file' }) }));
      else setUiSchema((prev) => ({ ...(prev || {}), fields: upsertUiFieldInList(((prev || {}) as UiSchemaJson).fields, ref, { widget: 'text_input' }) }));
    }
  };

  const openEditField = (f: FieldRow) => {
    setError('');
    setEditFieldId(f.id);
    setEditFieldLabel(String(f.label ?? f.name ?? f.id));
    setEditFieldType(f.type && FIELD_TYPES.includes(f.type as (typeof FIELD_TYPES)[number]) ? f.type : 'text');
    setEditFieldRequired(Boolean(f.required));
    const opts = Array.isArray(f.allowed_values) ? f.allowed_values.join(', ') : '';
    setEditFieldOptions(opts);
    setEditFieldDialogOpen(true);
  };

  const saveEditedField = () => {
    if (!formSchema || !editFieldId) return;
    const label = editFieldLabel.trim();
    if (!label) {
      setError('Label is required.');
      return;
    }
    const schema = JSON.parse(JSON.stringify(formSchema)) as { sections?: Array<{ fields?: FieldRow[] }> };
    const sections = schema.sections || [];
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const sec = sections[safeIdx];
    if (!sec?.fields) {
      setError('No section fields.');
      return;
    }
    const idx = sec.fields.findIndex((x) => x.id === editFieldId);
    if (idx < 0) {
      setError('Field not found.');
      return;
    }
    const row: FieldRow = {
      ...sec.fields[idx],
      id: editFieldId,
      label,
      type: editFieldType,
      required: editFieldRequired,
    };
    delete row.allowed_values;
    if (editFieldType === 'single_select' || editFieldType === 'multi_select') {
      const opts = editFieldOptions.split(',').map((s) => s.trim()).filter(Boolean);
      if (opts.length === 0) {
        setError('Select-type fields need at least one option (comma-separated).');
        return;
      }
      row.allowed_values = opts;
    }
    sec.fields[idx] = row;
    setFormSchema(schema as unknown as Record<string, unknown>);
    setEditFieldDialogOpen(false);
    setInfo('Field updated (save to persist).');
  };

  const confirmDeleteField = () => {
    if (!formSchema || !deleteFieldTarget) return;
    const schema = JSON.parse(JSON.stringify(formSchema)) as { sections?: Array<{ fields?: FieldRow[] }> };
    const sections = schema.sections || [];
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    const sec = sections[safeIdx];
    if (!sec?.fields) return;
    sec.fields = sec.fields.filter((x) => x.id !== deleteFieldTarget.id);
    setFormSchema(schema as unknown as Record<string, unknown>);
    setDeleteFieldOpen(false);
    setDeleteFieldTarget(null);
    setInfo('Field removed (save to persist).');
  };

  const confirmDeleteSection = () => {
    if (!formSchema) return;
    const sections = ([...(formSchema.sections as Array<Record<string, unknown>> | undefined) || []]) as Array<Record<string, unknown>>;
    if (sections.length <= 1) {
      setError('Cannot delete the last section. Add another section first.');
      setDeleteSectionOpen(false);
      return;
    }
    const safeIdx = Math.max(0, Math.min(selectedSectionIndex, sections.length - 1));
    sections.splice(safeIdx, 1);
    const nextIdx = Math.min(safeIdx, Math.max(0, sections.length - 1));
    setFormSchema({ ...formSchema, sections });
    setSelectedSectionIndex(nextIdx);
    setDeleteSectionOpen(false);
    setInfo('Section removed (save to persist).');
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          background: `linear-gradient(135deg, ${theme.palette.grey[900]} 0%, ${alpha(theme.palette.primary.dark, 0.95)} 100%)`,
          color: 'common.white',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <WidgetsOutlinedIcon sx={{ opacity: 0.9, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              Form canvas
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }} noWrap>
              Drag blocks to reorder · Save syncs <code style={{ fontSize: '0.75rem' }}>field_refs</code>
            </Typography>
          </Box>
          {selectedConfigName ? (
            <Chip
              size="small"
              label={selectedConfigName}
              sx={{
                ml: 0.5,
                maxWidth: 220,
                color: 'common.white',
                borderColor: alpha('#fff', 0.35),
                bgcolor: alpha('#fff', 0.1),
                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
              variant="outlined"
            />
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showPreview}
                onChange={(_, v) => setShowPreview(v)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: 'common.white' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: alpha('#fff', 0.35) },
                }}
              />
            }
            label={<Typography variant="body2" sx={{ color: 'common.white', opacity: 0.95 }}>Split preview</Typography>}
          />
          <Tooltip title="Open runtime in full window">
            <span>
              <IconButton
                size="small"
                sx={{ color: 'common.white' }}
                onClick={() => {
                  const id = activeConfigId || selectedConfigId;
                  if (!id) return;
                  setActiveConfigId(id);
                  window.history.pushState({}, '', `/runtime-form/${id}`);
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                disabled={!activeConfigId && !selectedConfigId}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            size="medium"
            onClick={save}
            disabled={!selectedConfigId || !formSchema}
            sx={{
              fontWeight: 600,
              bgcolor: 'common.white',
              color: 'primary.dark',
              '&:hover': { bgcolor: alpha('#fff', 0.92) },
            }}
          >
            Save canvas
          </Button>
        </Stack>
      </Box>

      {!activeTenantId && (
        <Alert severity="info" sx={{ m: 2, mb: 0 }}>
          Select a tenant in the header, then pick a config in the inspector.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ m: 2, mb: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ m: 2, mb: 0 }} onClose={() => setInfo('')}>
          {info}
        </Alert>
      )}
      {forcedAdvanced && (
        <Alert severity="warning" sx={{ m: 2, mb: 0 }} onClose={() => setForcedAdvanced(false)}>
          This form uses an advanced layout (multiple tabs/sub-tabs). Studio switched to <b>Advanced</b> mode to avoid losing structure.
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        alignItems="stretch"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box
            sx={{
              width: { xs: '100%', lg: STUDIO_PANEL.leftWidth },
              flexShrink: 0,
              borderRight: { lg: 1 },
              borderBottom: { xs: 1, lg: 0 },
              borderColor: 'divider',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
              px: 2,
              pt: 1.75,
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} color="primary">
              Studio mode
            </Typography>
            <TextField
              select
              size="small"
              value={studioMode}
              onChange={(e) => setStudioMode(e.target.value as 'simple' | 'advanced')}
              sx={{ minWidth: 140 }}
              disabled={isAdvancedLayoutDetected}
              helperText={isAdvancedLayoutDetected ? 'Advanced layout detected' : undefined}
            >
              <MenuItem value="simple">Simple</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
            </TextField>
          </Box>
          {studioMode === 'simple' ? (
            <SimpleBuilderInspectorPanel
              activeTenantId={activeTenantId}
              configs={configs}
              selectedConfigId={selectedConfigId}
              onSelectConfigId={(id) => {
                setSelectedConfigId(id);
                setActiveConfigId(id || null);
              }}
              formSchema={formSchema}
              selectedSectionIndex={selectedSectionIndex}
              onSelectSectionIndex={(idx) => {
                setSelectedSectionIndex(idx);
                setInspectorSelection({ kind: null });
              }}
              onOpenAddSection={() => setSectionDialogOpen(true)}
              onQuickAddFieldType={quickAddFieldType}
            />
          ) : (
            <AdvancedInspectorPanel
              activeTenantId={activeTenantId}
              configs={configs}
              selectedConfigId={selectedConfigId}
              onSelectConfigId={(id) => {
                setSelectedConfigId(id);
                setActiveConfigId(id || null);
              }}
              formSchema={formSchema}
              selectedSectionIndex={selectedSectionIndex}
              onSelectSectionIndex={setSelectedSectionIndex}
              autoLayoutMode={autoLayoutMode}
              onChangeAutoLayoutMode={setAutoLayoutMode}
              isAutoLayouting={isAutoLayouting}
              onRunAutoLayout={() => void runAutoLayout()}
              onOpenAutoLayoutHistory={() => {
                setAutoLayoutHistoryOpen(true);
                void loadAutoLayoutHistory();
              }}
              onReloadFromServer={() => {
                const id = activeConfigId || selectedConfigId;
                if (!id) return;
                setSelectedConfigId(id);
                void loadConfig(id);
              }}
              onOpenAddSection={() => setSectionDialogOpen(true)}
              onOpenAddField={() => setFieldDialogOpen(true)}
              onOpenDeleteSection={() => {
                setError('');
                setDeleteSectionOpen(true);
              }}
              canDeleteSection={Boolean(formSchema) && (((formSchema?.sections as unknown[] | undefined)?.length ?? 0) > 1)}
              canvasTabs={canvasTabs}
              onChangeTabs={setCanvasTabs}
              onInfo={setInfo}
              autoLayoutSource={autoLayoutSource}
              autoLayoutDecisions={autoLayoutDecisions}
              autoLayoutWarnings={autoLayoutWarnings}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{ flex: 1, minHeight: 0 }}
          >
            <Box
              sx={{
                flex: showPreview ? { md: '1 1 36%' } : 1,
                minWidth: 0,
                minHeight: 0,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'grey.100',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: theme.palette.grey[50],
                  backgroundImage: `radial-gradient(${alpha(theme.palette.primary.main, 0.14)} 1.25px, transparent 1.25px)`,
                  backgroundSize: '20px 20px',
                  backgroundPosition: '0 0',
                  overflow: 'hidden',
                  boxShadow:
                    'inset 0 1px 0 rgba(255,255,255,0.85), inset 0 0 80px rgba(1, 38, 118, 0.04)',
                }}
              >
                <Box sx={{ flexShrink: 0, px: 2.5, pt: 2.5, pb: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color="primary"
                      variant="filled"
                      label="Active frame"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      variant={inspectorSelection.kind === 'section' ? 'filled' : 'outlined'}
                      color="secondary"
                      label="Section props"
                      onClick={() => setInspectorSelection({ kind: 'section' })}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {currentSectionLabel}
                    </Typography>
                    {currentSectionStableId ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={currentSectionStableId}
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                      />
                    ) : null}
                  </Stack>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    px: 2.5,
                    pb: 2.5,
                  }}
                >
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                      <Stack spacing={1.25}>
                        {fields.map((f) => (
                          <SortableFieldRow
                            key={`${selectedSectionIndex}::${f.id}`}
                            id={`${selectedSectionIndex}::${f.id}`}
                            accentColor={fieldAccentColor(f.type, theme)}
                          >
                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                              <Box
                                sx={{
                                  minWidth: 0,
                                  cursor: 'pointer',
                                  outline:
                                    inspectorSelection.kind === 'field' && inspectorSelection.fieldId === f.id
                                      ? `2px solid ${theme.palette.primary.main}`
                                      : 'none',
                                  outlineOffset: 2,
                                  borderRadius: 1,
                                  pr: 0.5,
                                }}
                                onClick={() => setInspectorSelection({ kind: 'field', fieldId: f.id })}
                              >
                                <Typography variant="body2" fontWeight={700}>
                                  {f.label || f.id}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  component="div"
                                  sx={{ fontFamily: 'monospace', color: 'text.secondary', mt: 0.25 }}
                                >
                                  {f.id}
                                  <Box component="span" sx={{ mx: 0.75, opacity: 0.5 }}>|</Box>
                                  {f.type || 'text'}
                                  {f.required ? (
                                    <>
                                      <Box component="span" sx={{ mx: 0.75, opacity: 0.5 }}>|</Box>
                                      <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>required</Box>
                                    </>
                                  ) : null}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0}>
                                <Tooltip title="Edit field">
                                  <IconButton size="small" aria-label="Edit field" onClick={() => openEditField(f)}>
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove from frame">
                                  <IconButton
                                    size="small"
                                    aria-label="Delete field"
                                    onClick={() => {
                                      setError('');
                                      setDeleteFieldTarget({ id: f.id, label: f.label || f.id });
                                      setDeleteFieldOpen(true);
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Stack>
                          </SortableFieldRow>
                        ))}
                      </Stack>
                    </SortableContext>
                  </DndContext>

                  {fields.length === 0 && (
                    <Box
                      sx={{
                        py: 4,
                        px: 2,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: 'divider',
                        bgcolor: alpha(theme.palette.background.paper, 0.65),
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Drop zone empty
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Add fields with <strong>Add field</strong> or edit nested groups in uploaded JSON.
                      </Typography>
                    </Box>
                  )}

                  {groupFields.length > 0 && (
                    <Box sx={{ mt: 2.5 }}>
                      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                        Embedded groups (read-only)
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {groupFields.map((f) => (
                          <Paper
                            key={`${f.groupName || 'group'}:${f.id}`}
                            elevation={0}
                            sx={{
                              p: 1.25,
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: alpha(theme.palette.grey[500], 0.04),
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>{f.label || f.id}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {f.groupName ? `${f.groupName} · ` : ''}{f.id} · {f.type || 'text'}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                width: isPropsPanelOpen ? { xs: '100%', md: STUDIO_PANEL.propertiesWidth } : { xs: '100%', md: STUDIO_PANEL.inspectorCollapsedRail },
                flexShrink: 0,
                flex: { xs: '1 1 auto', md: isPropsPanelOpen ? '1 1 0%' : '0 0 auto' },
                minWidth: 0,
                borderLeft: { md: 1 },
                borderTop: { xs: 1, md: 0 },
                borderColor: 'divider',
                p: isPropsPanelOpen ? 2 : 0,
                overflow: 'hidden',
                bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: { md: '100%' },
              }}
            >
              {isPropsPanelOpen ? (
                <>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexShrink: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="primary">
                      Properties
                    </Typography>
                    <Tooltip title="Hide properties">
                      <IconButton
                        size="small"
                        onClick={() => setIsPropsPanelOpen(false)}
                        aria-label="Hide properties"
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      pr: 0.5,
                      pb: 1,
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                    {studioMode === 'simple' ? (
                      <SimplePropertiesPanel
                        selectionKind={
                          inspectorSelection.kind === 'field'
                            ? 'field'
                            : (inspectorSelection.kind === 'section' ? 'section' : 'none')
                        }
                        sectionStableId={currentSectionStableId}
                        sectionTitle={currentSectionLabel}
                        uiSection={selectedUiSectionRow}
                        schemaField={selectedSchemaField}
                        uiField={selectedUiField}
                        onPatchSchemaField={patchSchemaField}
                        onPatchUiField={patchUiField}
                        onPatchSection={patchSectionSimple}
                      />
                    ) : (
                      <CanvasPropertiesPanel
                        selection={inspectorSelection}
                        sectionStableId={currentSectionStableId}
                        uiField={selectedUiField}
                        uiSection={selectedUiSectionRow}
                        onPatchField={patchUiField}
                        onPatchSection={patchCurrentSectionUi}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <Stack
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    pt: 1,
                    pb: 1,
                  }}
                >
                  <Tooltip title="Show properties">
                    <IconButton
                      size="small"
                      onClick={() => setIsPropsPanelOpen(true)}
                      aria-label="Show properties"
                      sx={{
                        width: 34,
                        height: 34,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    sx={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      fontWeight: 700,
                      color: 'text.secondary',
                      userSelect: 'none',
                    }}
                  >
                    Properties
                  </Typography>
                </Stack>
              )}
            </Box>

            {showPreview && selectedConfigId && (
              <Box
                sx={{
                  flex: { md: '1 1 34%' },
                  minWidth: { md: STUDIO_PANEL.previewMinWidth },
                  minHeight: { xs: STUDIO_PANEL.previewMinHeight },
                  borderTop: { xs: 1, md: 0 },
                  borderLeft: { md: 1 },
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'background.paper',
                  alignSelf: 'stretch',
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.grey[500], 0.06),
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                    LIVE PREVIEW · RUNTIME FORM
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 420,
                    position: 'relative',
                    width: '100%',
                  }}
                >
                  <iframe
                    title="Runtime form preview"
                    src={previewIframeSrc}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      border: 0,
                      backgroundColor: theme.palette.grey[100],
                    }}
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      </Stack>

      <Dialog open={sectionDialogOpen} onClose={() => setSectionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add section</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Section id"
              value={newSectionId}
              onChange={(e) => setNewSectionId(e.target.value)}
              helperText="Stable id, e.g. patient_registration"
              fullWidth
            />
            <TextField
              size="small"
              label="Title"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addSection}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fieldDialogOpen} onClose={() => setFieldDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add field (current section)</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Field id"
              value={newFieldId}
              onChange={(e) => setNewFieldId(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Label"
              value={newFieldLabel}
              onChange={(e) => setNewFieldLabel(e.target.value)}
              fullWidth
            />
            <TextField select size="small" label="Type" value={newFieldType} onChange={(e) => setNewFieldType(e.target.value)} fullWidth>
              {FIELD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Required"
              value={newFieldRequired ? 'yes' : 'no'}
              onChange={(e) => setNewFieldRequired(e.target.value === 'yes')}
              fullWidth
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </TextField>
            {(newFieldType === 'single_select' || newFieldType === 'multi_select') && (
              <TextField
                size="small"
                label="Options (comma-separated)"
                value={newFieldOptions}
                onChange={(e) => setNewFieldOptions(e.target.value)}
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addField}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editFieldDialogOpen} onClose={() => setEditFieldDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit field</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField size="small" label="Field id" value={editFieldId} disabled fullWidth helperText="Id cannot change (stable keys)" />
            <TextField size="small" label="Label" value={editFieldLabel} onChange={(e) => setEditFieldLabel(e.target.value)} fullWidth />
            <TextField select size="small" label="Type" value={editFieldType} onChange={(e) => setEditFieldType(e.target.value)} fullWidth>
              {FIELD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Required"
              value={editFieldRequired ? 'yes' : 'no'}
              onChange={(e) => setEditFieldRequired(e.target.value === 'yes')}
              fullWidth
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </TextField>
            {(editFieldType === 'single_select' || editFieldType === 'multi_select') && (
              <TextField
                size="small"
                label="Options (comma-separated)"
                value={editFieldOptions}
                onChange={(e) => setEditFieldOptions(e.target.value)}
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFieldDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEditedField}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteFieldOpen} onClose={() => { setDeleteFieldOpen(false); setDeleteFieldTarget(null); }}>
        <DialogTitle>Delete field?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove &quot;{deleteFieldTarget?.label}&quot; (<code>{deleteFieldTarget?.id}</code>) from this section. Unsaved changes elsewhere are kept; click <b>Save</b> on the canvas to persist.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteFieldOpen(false); setDeleteFieldTarget(null); }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteField} disabled={!deleteFieldTarget}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteSectionOpen} onClose={() => setDeleteSectionOpen(false)}>
        <DialogTitle>Delete section?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove section &quot;{currentSectionLabel}&quot; and all of its top-level fields and groups. This cannot be undone except by reloading without saving. You must keep at least one section.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSectionOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteSection}>
            Delete section
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={autoLayoutHistoryOpen} onClose={() => setAutoLayoutHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auto-layout history</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Recent auto-layout runs for this config (from audit events). Use this to review modes and warnings.
          </DialogContentText>
          {autoLayoutHistoryLoading ? (
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          ) : autoLayoutHistoryItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No auto-layout events yet.</Typography>
          ) : (
            <Stack spacing={1}>
              {autoLayoutHistoryItems.slice(0, 30).map((it) => {
                const meta = (it.meta_json && typeof it.meta_json === 'object') ? it.meta_json : {};
                const mode = String(meta.mode || '');
                const decisions = Array.isArray(meta.decisions) ? meta.decisions : [];
                const warnings = Array.isArray(meta.warnings) ? meta.warnings : [];
                return (
                  <Paper key={String(it.id)} variant="outlined" sx={{ p: 1.25 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2" fontWeight={700}>
                        {mode ? `Mode: ${mode}` : 'Auto-layout'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {it.created_at ? new Date(it.created_at).toLocaleString() : ''}
                      </Typography>
                    </Stack>
                    {decisions.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {decisions.slice(0, 4).map((d: string, idx: number) => (
                          <Typography key={`${it.id}-d-${idx}`} variant="caption" color="text.secondary" display="block">
                            - {String(d)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {warnings.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {warnings.slice(0, 4).map((w: string, idx: number) => (
                          <Typography key={`${it.id}-w-${idx}`} variant="caption" color="warning.main" display="block">
                            ! {String(w)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => void loadAutoLayoutHistory()} disabled={!selectedConfigId || autoLayoutHistoryLoading}>
            Refresh
          </Button>
          <Button onClick={() => setAutoLayoutHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
